import { ChoiceGroup } from "@/components/ui/ChoiceGroup";
import { SettingsPanel } from "@/components/ui/SettingsPanel";
import { Button } from "@/components/ui/button";
import { Note } from "@/components/ui/Note";
import type { ClaimMarkerScheme } from "@patent-studio/api-types";
import { useSetMarkerScheme } from "@/hooks/use-claim-strategy";
import {
  useChooseClaimPreferences,
  useClaimPreferences,
} from "@/hooks/use-claim-preferences";
import { MARKER_SCHEMES, sampleMarkers } from "./marker-scheme-choices";

/** Each scheme shown as the markers it draws, and named for a reader. */
const LETTERINGS = MARKER_SCHEMES.map(({ scheme, name }) => ({
  label: sampleMarkers(scheme).join(" "),
  value: scheme,
  name,
}));

/**
 * The draft's claim settings, at the end of Claim Strategy's toolbar.
 *
 * The lettering chosen here is this draft's alone. It becomes the one new drafts start in
 * only when the attorney asks, with "Use for my new drafts": an attorney who changes one
 * draft's lettering does not silently change every draft they make afterwards. The set takes
 * a new lettering only while it can be written, as it takes every other change.
 */
export function ClaimSettingsMenu({
  taskId,
  chosen,
  editable,
}: {
  taskId: string;
  chosen: ClaimMarkerScheme;
  editable: boolean;
}) {
  const respell = useSetMarkerScheme(taskId);
  const { data: preferences } = useClaimPreferences();
  const keep = useChooseClaimPreferences();

  return (
    <SettingsPanel
      title="Claim settings"
      note="This draft's claims"
      closeLabel="Close claim settings"
    >
      <ChoiceGroup
        label="Claim line markers"
        choices={LETTERINGS}
        chosen={chosen}
        disabled={!editable || respell.isPending}
        onChoose={(scheme) => {
          respell.mutate(scheme);
        }}
      />
      {preferences !== undefined &&
        (preferences.marker_scheme === chosen ? (
          <Note className="mt-2.5">
            Your new drafts start in this lettering.
          </Note>
        ) : (
          <Button
            type="button"
            variant="link"
            size="dense"
            className="mt-2"
            disabled={keep.isPending}
            onClick={() => {
              keep.mutate(chosen);
            }}
          >
            Use for my new drafts
          </Button>
        ))}
    </SettingsPanel>
  );
}
